import { ChangeEventHandler, Dispatch, forwardRef, SetStateAction, useState } from 'react';

import { VisuallyHidden } from '@components/visually-hidden';
import { useEvent } from '@hooks/use-event';
import { fileNameFromPath } from '@utils/strings';
import { XOR } from '@utils/types';

import { AcceptType, fileTypeAcceptMap } from './types';

type FileInputBaseProps = {
  id?: string;
  isDisabled?: boolean;
  clearOnCancel?: boolean;
};

type FileInputSingleProps = FileInputBaseProps & {
  onChange(file: File | null): void;
};

type FileInputMultiProps = FileInputBaseProps & {
  isMulti: true;
  onChange(files: Array<File>): void;
};

export type FileInputProps = XOR<FileInputSingleProps, FileInputMultiProps> & AcceptType;

export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  ({ onChange, accept, fileType, isMulti, isDisabled, id, clearOnCancel }, ref) => {
    const handleChange = useEvent<ChangeEventHandler<HTMLInputElement>>((e) => {
      if (!e.target?.files?.length && !clearOnCancel) {
        return;
      }
      if (isMulti) {
        onChange(e.target.files ? Array.from(e.target.files) : []);
      } else {
        onChange(e.target.files ? e.target.files[0] : null);
      }
    });

    const actualAccept = fileType ? fileTypeAcceptMap[fileType] : accept;

    return (
      <VisuallyHidden>
        <input
          disabled={isDisabled}
          tabIndex={-1}
          multiple={isMulti}
          type="file"
          id={id}
          ref={ref}
          onChange={handleChange}
          accept={actualAccept}
        />
      </VisuallyHidden>
    );
  },
);

export type FileObject = { url: string; name: string; fullUrl?: string };
export type FileResponseObject = Pick<FileObject, 'url' | 'fullUrl'>;
export const transformFileObject = (file: FileResponseObject): FileObject => ({
  ...file,
  name: fileNameFromPath(file.url),
});

export type InstantUploadFileInputProps = FileInputBaseProps &
  AcceptType & {
    onSelect(file: File | null): Promise<FileResponseObject>;
    onChange(file: FileObject | null): void;
    defaultValue?: FileResponseObject;
    setFile?: Dispatch<SetStateAction<FileObject | null>>;
  };

export const InstantUploadFileInput = forwardRef<HTMLInputElement, InstantUploadFileInputProps>(
  ({ onChange, onSelect, setFile, ...rest }, ref) => {
    return (
      <FileInput
        {...rest}
        ref={ref}
        onChange={async (selectedFile) => {
          const data = (await onSelect(selectedFile)) as FileObject;
          const newFile = transformFileObject(data);
          onChange(newFile);
          setFile?.(newFile);
        }}
      />
    );
  },
);

export const useInstantFile = (defaultValue?: FileResponseObject) =>
  useState<FileObject | null>(defaultValue ? transformFileObject(defaultValue) : null);
